<template>
    <div class="max-w-xl mx-auto my-4 px-4">
        <div class="flex justify-between">
            <!-- Loop over steps -->
            <template v-for="(step, index) in visibleSteps" :key="'step-' + index">
                <!-- Step circle -->
                <div class="flex flex-col items-center" @click="handleStepClick(index)">
                    <div class="w-10 h-10 mx-10 rounded-full text-lg text-white flex items-center justify-center"
                        :class="getStepClass(index + 1)">
                        <span class="text-center w-full">
                            <font-awesome-icon v-if="index + 1 < visibleCurrentStep" icon="check"
                                class="text-white p-3" />
                            <span v-else class="p-3" :class="getStepTextClass(index + 1)">{{ index + 1 }}</span>
                        </span>
                    </div>

                    <!-- Step title directly under the step circle -->
                    <div :class="getStepTitleClass(index + 1)" class="text-xs text-center mt-2">
                        {{ step }}
                    </div>
                </div>

                <!-- Progress bar between steps, but not after the last step -->
                <div v-if="index < visibleSteps.length - 1" :class="getProgressBarContainerClass()"
                    class="align-center items-center content-center flex">
                    <div class="w-full bg-grey-light rounded items-center align-middle align-center flex-1">
                        <div :class="getProgressBarClass(index + 1)" :style="getProgressBarStyle(index + 1)"
                            class="text-xs leading-none py-1 text-center text-grey-darkest rounded"></div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        currentStep: {
            type: Number,
            required: true,
        },
        steps: {
            type: Array,
            required: true,
        },
        disableProductSelection: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        visibleSteps() {
            return this.disableProductSelection ? this.steps.slice(1) : this.steps;
        },
        visibleCurrentStep() {
            return this.disableProductSelection && this.currentStep > 1
                ? this.currentStep - 1
                : this.currentStep;
        },
    },
    emits: ['step-click'],
    methods: {
        getProgressBarContainerClass() {
            return this.visibleSteps.length === 2 ? 'w-4/6' : 'w-2/6';
        },
        getStepClass(step) {
            if (step < this.visibleCurrentStep) {
                return 'bg-green scale-110 transform transition-transform duration-300 ease-in-out cursor-pointer';
            } else if (step === this.visibleCurrentStep) {
                return 'border-2 border-green text-green scale-110 transform transition-transform duration-300 ease-in-out';
            } else {
                return 'bg-white border-2 border-grey-light transition-transform duration-300 ease-in-out';
            }
        },
        handleStepClick(index) {
            const stepNumber = index + 1;
            // Only allow clicking on completed steps or current step
            if (stepNumber <= this.currentStep) {
                this.$emit('step-click', this.disableProductSelection ? stepNumber + 1 : stepNumber);
            }
        },
        getStepTextClass(step) {
            return step === this.visibleCurrentStep ? 'text-green' : 'text-grey-darker';
        },
        getStepTitleClass(step) {
            return step === this.visibleCurrentStep ? 'font-bold text-green opacity-100 transition-opacity duration-500' : 'opacity-50 transition-opacity duration-500';
        },
        getProgressBarClass(step) {
            if (step < this.visibleCurrentStep) {
                return 'bg-green-light transition-all duration-500 ease-in-out';
            } else if (step === this.visibleCurrentStep) {
                return 'bg-green-light transition-all duration-500 ease-in-out';
            } else {
                return 'bg-grey-light transition-all duration-500 ease-in-out';
            }
        },
        getProgressBarStyle(step) {
            if (step < this.visibleCurrentStep) {
                return { width: '100%' };
            } else if (step === this.visibleCurrentStep) {
                return { width: '20%' };
            } else {
                return { width: '0%' };
            }
        },
    },
};
</script>


<style scoped>
.text-green {
    color: #6366f1; /* Indigo color */
}

.border-green {
    border-color: #6366f1; /* Indigo color */
}

.text-grey-darker {
    color: #6b7280;
}

.bg-green {
    background-color: #6366f1; /* Indigo color */
}

.bg-grey-light {
    background-color: #e5e7eb;
}

.bg-green-light {
    background-color: #818cf8; /* Lighter indigo */
}

.border-grey-light {
    border-color: #e5e7eb;
}

.text-grey-darkest {
    color: #111827;
}
</style>
